/**
 * 路由权限守卫
 * @author AI Assistant
 * @date 2024-01-01
 */

import { createRouter, createWebHistory, type Router, type RouteRecordRaw } from 'vue-router'
import { ElMessage } from 'element-plus'
import { SITE_NAME } from '@/constants/site'
import { useUserStore } from '@/stores/user'
import { usePermissionStore } from '@/stores/permission'
import { useTabsStore } from '@/stores/tabs'
import { useNavigationStore } from '@/stores/navigation'
import { constantRoutes } from './index'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

// 路由调试日志
const routeLog = (message: string) => {
  console.log(`[router] ${message}`)
}

/**
 * 匹配动态路由
 * 用于比较路由路径模式与实际URL路径
 * 例如：'/user/:id' 应该匹配 '/user/123'
 * @param routePath 路由路径模式
 * @param actualPath 实际URL路径
 * @returns 是否匹配
 */
const matchDynamicRoute = (routePath: string, actualPath: string): boolean => {
  // 将路由路径转换为正则表达式
  // 例如：'/user/:id' => /^\/user\/[^\/]+$/
  const pattern = routePath
    .replace(/\//g, '\\/')
    .replace(/:[^/]+/g, '[^\\/]+')

  const regex = new RegExp(`^${pattern}$`)
  return regex.test(actualPath)
}

// 配置 NProgress
NProgress.configure({
  showSpinner: false,
  minimum: 0.1,
  speed: 200
})

// 白名单路由（不需要权限验证）
const WHITE_LIST = [
  '/login',
  '/register',
  '/forgot-password',
  '/404',
  '/403',
  '/500'
]

/**
 * 检查路由权限
 * @param route 路由对象
 * @param permissions 用户权限列表
 * @returns 是否有权限
 */
const checkRoutePermission = (route: any, permissions: string[]): boolean => {
  // 检查权限数组是否有效
  if (!permissions || !Array.isArray(permissions)) {
    return false
  }

  // 超级管理员权限检查 - 拥有通配符权限可以访问所有路由
  if (permissions.includes('*')) {
    return true
  }

  // 如果路由没有设置权限要求，默认允许访问
  if (!route.meta?.auth && !route.meta?.permissions) {
    return true
  }

  // 优先检查 permissions 字段，然后检查 auth 字段
  const authField = route.meta?.permissions || route.meta?.auth
  if (!authField) {
    return true
  }

  const requiredPermissions = Array.isArray(authField)
    ? authField
    : [authField]

  // 检查用户是否拥有所需权限
  const hasPermission = requiredPermissions.some((permission: string) => {
    const hasThis = permissions.includes(permission)
    return hasThis
  })

  return hasPermission
}

/**
 * 检查路径是否存在于任一模块路由配置（未按权限过滤的全量集合）
 * 用于区分"已登录但无权限"(403)与"路径不存在"(404)
 * 注意：模块配置中子路由是相对路径（如 'publish'），必须拼接父路径后再匹配，
 * 与实际注册逻辑（module-config 的 fullPath = parentPath + '/' + path）保持一致
 * @param path 实际 URL 路径
 * @returns 是否为已知模块路径
 */
const isKnownModulePath = async (path: string): Promise<boolean> => {
  const { loadAllModuleConfigs } = await import('@/utils/module-config')
  const configs = await loadAllModuleConfigs()
  const match = (items: any[], parent: string): boolean =>
    items.some(item => {
      if (!item?.path) return false
      const full = item.path.startsWith('/') ? item.path : `${parent}/${item.path}`.replace(/\/{2,}/g, '/')
      if (full === path) return true
      if (full.includes(':') && matchDynamicRoute(full, path)) return true
      if (Array.isArray(item.children) && item.children.length > 0 && match(item.children, full)) return true
      return false
    })
  return configs.some(config => config.enabled !== false && match(config.routes || [], ''))
}

/**
 * 设置路由权限守卫
 * @param router 路由实例
 */
export const setupPermissionGuard = (router: Router): void => {
  router.beforeEach(async (to, _from, next) => {
    // 开始进度条
    NProgress.start()

    // 获取store实例
    const userStore = useUserStore()
    const token = userStore.token
    const permissionStore = usePermissionStore()

    // 首先检查是否在白名单中
    if (WHITE_LIST.includes(to.path)) {
      routeLog('路径在白名单中，允许访问')
      next()
      return
    }

    // 工作流设计器统一按鉴权流程处理

    // 如果用户未登录，直接重定向到登录页
    if (!token) {
      routeLog('用户未登录，重定向到登录页')

      // 立即跳转到登录页
      next({ path: '/login', query: { redirect: to.fullPath, cause: '401' } })

      // 异步清除用户状态（不阻塞路由跳转）
      userStore.logout(false, true).catch(console.error)
      return
    }

    try {
      // 如果用户已登录
      if (token) {
        // 如果访问登录页，重定向到首页
        if (to.path === '/login') {
          routeLog('已登录用户访问登录页，重定向到首页')
          next({ path: '/' })
          return
        }

        // 通配符兜底：路径未注册时进入（动态权限路由可能尚未生成，或用户信息缺失导致路由被过滤）
        if (to.name === 'CatchAll') {
          routeLog(`捕获到未匹配路径: ${to.path}`)

          try {
            // 有 token 但缺用户信息（如本地缓存损坏），重新拉取；
            // 拉取失败说明 token 已失效，重新登录
            if (!userStore.userInfo) {
              const info = await userStore.getUserInfo()
              if (!info) {
                routeLog('token 已失效，重新登录')
                await userStore.logout(false, true)
                next({ path: '/login', query: { redirect: to.fullPath, cause: '401' } })
                return
              }
            }

            // 仅在权限路由尚未生成过时才重新生成（非管理员生成含 500ms 模拟延迟）；
            // 已生成过（addRoutes 非空）说明上次已按当前用户权限注册，路径仍未匹配即真无权限/不存在
            if (permissionStore.addRoutes.length === 0 || !userStore.userInfo) {
              const accessRoutes = await permissionStore.generateRoutes()
              addDynamicRoutes(router, accessRoutes)
            }
            // 仅当目标已能匹配真实路由时才重试（防循环）；next 对象语法 path 不能携带 query
            const resolved = router.resolve(to.fullPath)
            if (resolved.name !== 'CatchAll') {
              routeLog(`路由已生成，重试导航: ${to.path}`)
              next({ path: to.path, query: to.query, hash: to.hash, replace: true })
              return
            }

            // 仍未匹配：存在于模块配置 → 无权限(403)，否则 → 404
            const known = await isKnownModulePath(to.path)
            if (known) {
              routeLog('路径存在但无权限，跳转到403')
              ElMessage.error('您没有访问该页面的权限')
              next({ path: '/403' })
            } else {
              routeLog('路径不存在，跳转到404')
              next({ path: '/404' })
            }
            return
          } catch (error) {
            console.error('生成权限路由失败:', error)
            ElMessage.error('权限路由加载失败')
            next({ path: '/403' })
            return
          }
        }

        // 检查是否需要添加动态路由
        if (permissionStore.addRoutes.length === 0) {
          try {
            routeLog('权限路由未生成，正在生成...')
            const accessRoutes = await permissionStore.generateRoutes()
            addDynamicRoutes(router, accessRoutes)

            // 检查目标路由是否存在
            const routeExists = router.hasRoute(to.name as string)
            if (routeExists) {
              next({ ...to, replace: true })
            } else {
              routeLog('目标路由不存在，重定向到首页')
              next({ path: '/home' })
            }
            return
          } catch (error) {
            console.error('生成权限路由失败:', error)
            ElMessage.error('权限路由加载失败')
            next({ path: '/403' })
            return
          }
        }

        // 检查路由权限
        const hasPermission = checkRoutePermission(to, userStore.permissions)
        routeLog(`权限检查: ${to.path} - ${hasPermission ? '通过' : '拒绝'}`)

        if (hasPermission) {
          next()
        } else {
          routeLog('权限不足，跳转到403页面')
          ElMessage.error('您没有访问该页面的权限')
          next({ path: '/403' })
        }
      }
    } catch (error) {
      console.error('路由守卫错误:', error)
      // 发生错误时，清除用户信息并重定向到登录页
      await userStore.logout()
      next({ path: '/login' })
    }
  })

  router.afterEach((to) => {
    // 结束进度条
    NProgress.done()

    // 设置页面标题
    const title = to.meta?.title as string
    if (title) {
      document.title = `${title} - ${SITE_NAME}`
    } else {
      document.title = SITE_NAME
    }

    // 更新标签页和面包屑导航
    const tabsStore = useTabsStore()
    const navigationStore = useNavigationStore()

    // 添加标签页
    tabsStore.addTab(to)

    // 更新面包屑
    navigationStore.updateBreadcrumb(to)
  })

  // 路由错误处理
  router.onError((error) => {
    console.error('路由错误:', error)
    NProgress.done()
    ElMessage.error('页面加载失败，请刷新重试')
  })
}

/**
 * 动态添加路由
 *
 * 设计说明：
 * 1) 为避免在一次性 addRoute(含 children) 时出现 matcher 合并/覆盖的边界行为，
 *    改为“先挂父，再显式逐个挂子路由”的方式，保证子路由稳定挂载。
 * 2) 添加详尽调试日志，便于在刷新直达子路由时快速确认挂载前后路由树的实际状态。
 *
 * @param router 路由实例
 * @param routes 需要动态添加的路由数组（通常为权限过滤后的异步路由）
 */
export const addDynamicRoutes = (router: Router, routes: RouteRecordRaw[]): void => {
  try {
    console.groupCollapsed('🧩 addDynamicRoutes: 开始动态添加路由')

    // 添加前快照
    void router.getRoutes()

    routes.forEach((route) => {
      const routeName = route.name ? String(route.name) : undefined
      const routeExistsByName = routeName ? router.hasRoute(routeName) : false
      const routeExistsByPath = router.getRoutes().some(r => r.path === route.path)

      if (!routeExistsByName && !routeExistsByPath) {
        router.addRoute(route)
        return
      }

      if (Array.isArray(route.children) && route.children.length > 0) {
        const parentRoute = router.getRoutes().find(r => r.path === route.path || (routeName ? String(r.name) === routeName : false))
        const parentName = parentRoute?.name ? String(parentRoute.name) : routeName

        route.children.forEach((child) => {
          const childExistsByName = child.name ? router.hasRoute(String(child.name)) : false
          const childExistsByPath = router.getRoutes().some(r => r.path === child.path)
          if (childExistsByName || childExistsByPath) {
            return
          }

          if (parentName && router.hasRoute(parentName)) {
            router.addRoute(parentName, child)
          } else {
            router.addRoute(child)
          }
        })
      }

    })

    // 添加后快照
    const afterRoutes = router.getRoutes().map(r => ({ name: r.name, path: r.path }))

    // 可选：列出关键前缀的路由，帮助定位（例如 /tenant 与 /system）
    const important = afterRoutes.filter(r => typeof r.path === 'string' && (r.path.startsWith('/tenant') || r.path.startsWith('/system')))
    console.table(important)

    console.groupEnd()
  } catch (e) {
    console.groupEnd()
    console.error('addDynamicRoutes: 挂载动态路由时发生异常', e)
    throw e
  }
}

/**
 * 重置路由
 * @param router 路由实例
 */
export const resetRouter = (router: Router): void => {
  // 重置路由器，移除所有动态添加的路由
  const newRouter = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: constantRoutes
  })

  // 替换路由实例的matcher
  ;(router as any).matcher = (newRouter as any).matcher
}

/**
 * 检查用户是否有指定权限
 * @param permission 权限标识
 * @returns 是否有权限
 */
export const hasPermission = (permission: string): boolean => {
  const permissionStore = usePermissionStore()
  return permissionStore.permissions.some(p => p.code === permission)
}

/**
 * 检查用户是否有指定角色
 * @param role 角色标识
 * @returns 是否有角色
 */
export const hasRole = (role: string): boolean => {
  const userStore = useUserStore()
  return userStore.roles.includes(role)
}

/**
 * 检查用户是否有任意一个权限
 * @param permissions 权限列表
 * @returns 是否有权限
 */
export const hasAnyPermission = (permissions: string[]): boolean => {
  return permissions.some(permission => hasPermission(permission))
}

/**
 * 检查用户是否有所有权限
 * @param permissions 权限列表
 * @returns 是否有权限
 */
export const hasAllPermissions = (permissions: string[]): boolean => {
  return permissions.every(permission => hasPermission(permission))
}

/**
 * 检查用户是否有任意一个角色
 * @param roles 角色列表
 * @returns 是否有角色
 */
export const hasAnyRole = (roles: string[]): boolean => {
  return roles.some(role => hasRole(role))
}

/**
 * 检查用户是否有所有角色
 * @param roles 角色列表
 * @returns 是否有角色
 */
export const hasAllRoles = (roles: string[]): boolean => {
  return roles.every(role => hasRole(role))
}

// 导出权限检查函数供模板使用
export default {
  hasPermission,
  hasRole,
  hasAnyPermission,
  hasAllPermissions,
  hasAnyRole,
  hasAllRoles
}
